import React, { Component } from "react";
import { bool, func, shape, string } from "prop-types";
import Modal from "react-modal";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Form,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  Col,
} from "reactstrap";
import filesize from "filesize";
import swal from "sweetalert2";
import { Formik } from "formik";
import usersApi from "@/api/users";
import LoadingButtonIcon from "@/views/Components/LoadingButtonIcon";
import { handleError } from "@/util/errorHandler";
import { getBase64 } from "@/util/fileToBase64";
import UploadButton from "./UploadButton";
import styles from "./styles.module.scss";
import { MAX_FILE_SIZE_IN_BYTES } from "@/store/constants";

class KeyRecoveryModal extends Component {
  state = {
    file: null,
    isSending: false,
    code: null,
  };

  onSelectFile = file => {
    this.setState({ file });
  };

  onClose = async () => {
    if (this.state.code) {
      const isConfirmed = await this.askCloseConfirmation();

      if (!isConfirmed) return;
    }

    this.setState({ file: null, code: null });
    this.props.onModalClose();
  };

  askCloseConfirmation = async () => {
    const { value } = await swal({
      title: "Are you sure?",
      text: "After closing the window, you will not see the code again.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Close",
      cancelButtonText: "Do not close",
    });

    return value;
  };

  onSendRequest = async ({ documentId, name }) => {
    try {
      this.setState({ isSending: true });

      const { accountId } = this.props.userData;

      const { code } = await usersApi.sendKeyRecoveryRequest({
        accountId,
        documentId,
        name,
        application: await getBase64(this.state.file),
      });

      this.setState({ code });
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isSending: false });
    }
  };

  validateFormik = isOrganisation => ({ name, documentId }) => {
    const errors = {};

    if (isOrganisation) {
      if (!name) {
        errors.name = "Please enter user's organization name";
      }

      if (!documentId) {
        errors.documentId = "Please enter user's account ID";
      }

      if (
        documentId &&
        documentId.length &&
        !/^[a-z_0-9]{1,32}@[a-z_0-9]{1,9}$/g.test(documentId)
      ) {
        errors.documentId = "Invalid account ID";
      }
    } else {
      if (!name) {
        errors.name = "Please enter user's full name";
      }

      if (!documentId) {
        errors.documentId = "Please enter user's document ID";
      }

      if (
        documentId &&
        documentId.length &&
        !/^[a-zA-Z0-9]+$/g.test(documentId)
      ) {
        errors.documentId =
          "Incorrect document ID. The document ID should contain [a-zA-Z_0-9] symbols";
      }
    }

    if (!this.state.file) {
      errors.file = "Please attach the application";
    }

    return errors;
  };

  render() {
    const { isSending, code } = this.state;
    const { isOpen, userData, organization } = this.props;

    const firstStep = (
      <Formik
        initialValues={{
          name: organization ? organization : userData.fullName,
          documentId: organization
            ? userData.accountId
            : userData.documentId === "Not provided"
            ? ""
            : userData.documentId,
        }}
        validate={this.validateFormik(!!organization)}
        onSubmit={this.onSendRequest}>
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          errors,
          setErrors,
        }) => (
          <Form onSubmit={handleSubmit}>
            <CardBody>
              <p>
                Please ensure that the user's documents match his identity, scan
                his application and attach the file to the form below.
              </p>
              <p>
                The file must be png format and its size must not exceed{" "}
                {filesize(MAX_FILE_SIZE_IN_BYTES)}.
              </p>
              <FormGroup row>
                <Label sm={3}>{organization ? "Organization" : "Name"}</Label>

                <Col sm={9}>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Enter user's full name"
                    invalid={Boolean(errors.name)}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.name && (
                    <FormFeedback className="text-alert">
                      {errors.name}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>
                  {organization ? "Account ID" : "Document ID"}
                </Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="documentId"
                    placeholder="Enter user's document ID"
                    invalid={Boolean(errors.documentId)}
                    value={values.documentId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.documentId && (
                    <FormFeedback className="text-alert">
                      {errors.documentId}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Application</Label>
                <Col sm={9}>
                  <UploadButton
                    onSelectFile={file => {
                      setErrors({ ...errors, file: undefined });
                      this.onSelectFile(file);
                    }}
                    accept="image/png"
                    maxFileSize={MAX_FILE_SIZE_IN_BYTES}
                  />
                  {errors.file && (
                    <FormFeedback
                      className="text-alert"
                      style={{ display: "block" }}>
                      {errors.file}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
            </CardBody>
            <CardFooter>
              <div className={styles.footer}>
                <Button color="primary" type="submit">
                  <span>Display code</span>
                  <LoadingButtonIcon loading={isSending} />
                </Button>
                <Button onClick={this.onClose}>Cancel</Button>
              </div>
            </CardFooter>
          </Form>
        )}
      </Formik>
    );

    const secondStep = (
      <>
        <CardBody>
          <p className={styles.codeDescription}>
            Report the code below to the user.
          </p>
          <h1 className={styles.code}>{code}</h1>
        </CardBody>
        <CardFooter>
          <Button color="primary" onClick={this.onClose}>
            Close
          </Button>
        </CardFooter>
      </>
    );

    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={this.onClose}
        className={styles.modal}
        overlayClassName={styles.overlay}
        htmlOpenClassName={styles.htmlOpen}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}>
        <Card>
          <CardHeader>
            <Button
              close
              onClick={this.onClose}
              className={styles.closeButton}
            />
            <span>
              Key recovery for {userData.fullName || userData.accountId}
            </span>
          </CardHeader>
          {!code ? firstStep : secondStep}
        </Card>
      </Modal>
    );
  }
}

KeyRecoveryModal.propTypes = {
  isOpen: bool.isRequired,
  onModalClose: func.isRequired,
  userData: shape({
    accountId: string,
    fullName: string,
    documentId: string,
  }).isRequired,
  organization: string,
};

export default KeyRecoveryModal;
